"use client";

import { zodResolver } from "@hookform/resolvers/zod";
import Image from "next/image";
import { allowCheckout } from "@/app/_module/utils/nativeCheckoutGuard";
import { useRouter, useSearchParams } from "next/navigation";
import { useEffect, useState, useRef } from "react";
import { KeyRound } from "lucide-react";
import { isNativePlatform } from "@/lib/capacitor/platform";
import { useForm } from "react-hook-form";
import { HoneypotField } from "@/app/_module/components/HoneypotField";
import { clearSessionStorage } from "@/app/_module/utils/safeStorage";

import FormInput from "@/app/_module/components/FormInput";
import LoadingSpinner from "@/app/_module/components/LoadingSpinner";
import { Button } from "@/app/_module/components/ui/button";
import { Form, FormField } from "@/app/_module/components/ui/form";
import { routes } from "@/app/_module/constants/routes";
import { DIALER_CONSOLE_PATH, isDialerHost } from "@/app/_module/utils/dialerHost";
import { DIRECT130_APPLY_URL, DIRECT130_BOX, DIRECT130_BUTTON, DIRECT130_SIGN_IN_URL } from "@/app/_module/utils/direct130Host";
import { useDirect130Host } from "@/app/_module/utils/direct130HostContext";
import { useToast } from "@/app/_module/hooks/use-toast";
import { useDeviceDetection } from "@/app/_module/hooks/useDeviceDetection";
import { GoogleIcon, MicrosoftIcon } from "@/app/_module/icons";
import { getSupabaseSession, requestPasswordReset, getOrCreateDeviceId } from "@/app/_module/services/auth";
import supabaseAuthService, { NETWORK_BLOCKED_MESSAGE } from "@/app/_module/services/supabaseAuthService";
import { oauthService } from "@/app/_module/services/oauthService";
import { userServices } from "@/app/auth/sign-in/_modules/services/user-services";
import { useUserContext } from "@/app/_module/store/contexts/userContext/userContexts";
import { useBranding } from "@/app/_module/store/contexts/brandingContext";

import type { UserProfile } from "../_modules/types/user";

import AgencySelection from "./_modules/components/AgencySelection";
import TwoFactorAuth from "./_modules/components/TwoFactorAuth";
import TurnstileWidget, { type TurnstileWidgetRef } from "./_modules/components/TurnstileWidget";
import type { TLoginSchema } from "./_modules/schema/signin-schema";
import { loginSchema } from "./_modules/schema/signin-schema";

// Define specific types for login to avoid conflicts
interface LoginAgencyWithDetails {
  agency: {
    id: string;
    name: string;
    description: string;
    contactEmail: string;
    isActive: boolean;
    parentAgencyId: string | null;
    createdAt: string;
    updatedAt: string;
    deletedAt: string | null;
  };
  role: string;
  permissions: string[];
}

interface LoginResponse {
  user: {
    id: string;
    name: string;
    email: string;
    title: string;
    userType: string;
    profileImageUrl?: string;
    profile_image_url?: string;
  };
  agencies: LoginAgencyWithDetails[];
  isMultiAgencyUser: boolean;
  isIndependentAgent: boolean;
}

// Direct 130's notice says why the session ended; any other reason or ?message= shows nothing. It used to say
// "You were signed out" for all of them, so a tab-close sign-out read as a bug to someone who'd done nothing.
const DIRECT130_SIGNED_OUT: Record<string, string> = {
  tab_closed: "Closing the CRM tab signs you out, for security.",
  logged_in_elsewhere: "The CRM was opened on another device, so this one was signed out.",
  network_blocked: NETWORK_BLOCKED_MESSAGE,
  idle_timeout: "You were signed out after a while with no activity.",
  session_expired: "Your session expired.",
  logged_out: "You signed out.",
};

const DIRECT130_CARD = "rounded-2xl border border-[#d8e3df] bg-white p-6 sm:p-8 shadow-[0_1px_2px_#1025300a,0_28px_56px_-36px_#10253052]";

const LoginScreen = function () {
  // Native shell (Capacitor). Detected in an effect so the server render and
  // the first client paint agree — reading window during render would cause a
  // hydration mismatch. Flips one frame after mount; only cosmetic bits and the
  // trial CTA hang off it, so the flicker is invisible.
  // Native-vs-web layout is NOT React state any more. It used to be — set in an
  // effect after a dynamic import — which meant the server-rendered HTML painted
  // the web card first and the Capacitor shell snapped to the native layout a
  // beat later. Now <head> stamps `html.native-app` before <body> parses and
  // globals.css keys the whole native treatment off that class (.native-only /
  // .web-only / .native-center + the `html.native-app .native-auth-scope` rules).
  // Same DOM on server and client, first paint is already right.
  const navigate = useRouter();
  const searchParams = useSearchParams();
  const { toast } = useToast();
  const { refetchUser } = useUserContext();
  useDeviceDetection(); // Keep hook for potential future use
  const { branding, isWhiteLabeled } = useBranding();
  const [show2FA, setShow2FA] = useState(false);
  const [showLogoutNotification, setShowLogoutNotification] = useState(false);
  const [logoutReason, setLogoutReason] = useState<string | null>(null);
  const [showAgencySelection, setShowAgencySelection] = useState(false);
  const [isAgencySelectionMode, setIsAgencySelectionMode] = useState(false);
  const [userId, setUserId] = useState<string>("");
  const [loginData, setLoginData] = useState<LoginResponse | null>(null);
  const [userMfaSettings, setUserMfaSettings] = useState<any>(null);
  const [error, setError] = useState("");
  const [turnstileToken, setTurnstileToken] = useState<string>("");
  const [isTurnstileValid, setIsTurnstileValid] = useState(false);
  const [loginAttempts, setLoginAttempts] = useState(0);
  const [isLocked, setIsLocked] = useState(false);
  const [lockoutTime, setLockoutTime] = useState<Date | null>(null);
  const [lockoutRemainingSeconds, setLockoutRemainingSeconds] = useState(0);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotPasswordEmail, setForgotPasswordEmail] = useState("");
  const [forgotPasswordLoading, setForgotPasswordLoading] = useState(false);
  const [forgotPasswordMessage, setForgotPasswordMessage] = useState("");
  // Honeypot — stays "" for real users; bots auto-fill the hidden field.
  const [honeypot, setHoneypot] = useState("");
  const turnstileRef = useRef<TurnstileWidgetRef>(null);

  const form = useForm<TLoginSchema>({
    resolver: zodResolver(loginSchema),
    mode: "onChange",
    defaultValues: {
      username: "",
      password: "",
    },
  });

  const {
    handleSubmit,
    control,
    formState: { errors, isValid, isSubmitting },
  } = form;

  // Handle Turnstile token
  const handleTurnstileToken = (token: string) => {
    // Clear the "complete the security verification" error when a token ARRIVES, not on every call:
    // in development the widget re-sends its bypass token on every render (its effect depends on
    // this function), which wiped any error the instant it showed, e.g. a failed passkey sign-in.
    if (token && !turnstileToken) setError("");
    setTurnstileToken(token);
    setIsTurnstileValid(!!token);
  };

  // Handle Turnstile error
  const handleTurnstileError = (error: string) => {
    // Don't show Turnstile errors during agency selection
    if (!isAgencySelectionMode) {
      setError(error);
      setIsTurnstileValid(false);
    } else {
      // Clear Turnstile state during agency selection
      setTurnstileToken("");
      setIsTurnstileValid(false);
    }
  };

  // Check if account is locked (local state)
  const checkLockout = () => {
    if (lockoutTime && new Date() < lockoutTime) {
      const remainingTime = Math.ceil((lockoutTime.getTime() - new Date().getTime()) / 1000 / 60);
      setError(`Account temporarily locked. Try again in ${remainingTime} minutes.`);
      return true;
    }
    return false;
  };

  // Check lockout status from backend (persisted across page refresh/new tabs)
  // This ensures lockout state is enforced even after page refresh
  // Returns true if account is locked (caller should prevent login)
  const checkBackendLockoutStatus = async (email: string): Promise<boolean> => {
    if (!email || email.length < 3) return false;

    try {
      const result = await supabaseAuthService.checkLockoutStatus(email);

      if (result.locked && result.lockoutMinutes) {
        const lockoutUntil = new Date(Date.now() + result.lockoutMinutes * 60 * 1000);
        setLockoutTime(lockoutUntil);
        setIsLocked(true);
        setLoginAttempts(5);
        setError(`Account locked due to too many failed attempts. Try again in ${result.lockoutMinutes} minutes.`);
        return true; // Account is locked - prevent login
      } else if (result.remainingAttempts !== undefined) {
        // Update local attempts based on backend state
        const backendAttempts = 5 - result.remainingAttempts;
        if (backendAttempts > loginAttempts) {
          setLoginAttempts(backendAttempts);
        }
      }
      return false; // Not locked
    } catch (error) {
      // Silently fail - local lockout will still work
      console.warn('Failed to check backend lockout status:', error);
      return false;
    }
  };


  // Auto-clear lockout when time passes and update countdown every second
  useEffect(() => {
    if (!lockoutTime) {
      setLockoutRemainingSeconds(0);
      return;
    }

    // Calculate initial remaining seconds
    const updateRemaining = () => {
      const now = new Date();
      const remaining = Math.max(0, Math.ceil((lockoutTime.getTime() - now.getTime()) / 1000));
      setLockoutRemainingSeconds(remaining);

      if (remaining <= 0) {
        setIsLocked(false);
        setLoginAttempts(0);
        setLockoutTime(null);
        setError("");
      }
    };

    // Update immediately
    updateRemaining();

    // Then update every second for real-time countdown
    const timer = setInterval(updateRemaining, 1000);
    return () => clearInterval(timer);
  }, [lockoutTime]);

  // Clear Turnstile state when agency selection starts
  useEffect(() => {
    if (showAgencySelection) {
      setIsAgencySelectionMode(true);
      setTurnstileToken("");
      setIsTurnstileValid(false);
      setError(""); // Clear any Turnstile-related errors
    } else {
      setIsAgencySelectionMode(false);
    }
  }, [showAgencySelection]);

  // Flag when this login is part of a "Sign in with InsuraCentral" (OIDC) flow,
  // so the 2FA handler knows to capture the access token for the bridge.
  useEffect(() => {
    const next = searchParams.get('next') || '';
    // Only accept a SAME-ORIGIN /oauth/bridge target (prevents open redirect via
    // absolute or protocol-relative URLs that merely contain the substring).
    let safeNext = '';
    try {
      const u = new URL(next, window.location.origin);
      if (u.origin === window.location.origin && u.pathname.startsWith('/oauth/bridge')) {
        safeNext = u.toString();
      }
    } catch { /* invalid URL — ignore */ }
    if (safeNext) {
      try {
        sessionStorage.setItem('oidc_sso_pending', '1');
        sessionStorage.setItem('oidc_sso_next', safeNext); // validated bridge URL
      } catch {}
    }
  }, [searchParams]);

  // The page a Direct 130 sign-in lands on (its dashboard links straight to Quote & Enroll, leads, credits): a same-origin
  // path from the one-time link's hash, else the dashboard. A ref, not storage: the dashboard exit clears session storage.
  const direct130Next = useRef("");

  // Where to go once the session exists. A "Sign in with InsuraCentral" (OIDC)
  // login must return to the /oauth/bridge it came from so the grant completes
  // and the user lands back on the forum / Licensedproducer — not on the
  // dashboard. The bridge auto-continues (no account chooser) because the
  // login is seconds old. Every other login goes to the dashboard.
  const finishLogin = () => {
    let bridge = '';
    try {
      const u = new URL(sessionStorage.getItem('oidc_sso_next') || '', window.location.origin);
      if (u.origin === window.location.origin && u.pathname.startsWith('/oauth/bridge')) bridge = u.toString();
    } catch { /* ignore */ }
    if (bridge) {
      try {
        sessionStorage.removeItem('oidc_sso_next');
        sessionStorage.setItem('oidc_sso_autocontinue', '1');
      } catch { /* ignore */ }
      window.location.href = bridge;
      return;
    }
    // The unlimited dialer's own address lands on the dialer console.
    window.location.href = direct130Next.current || (isDialerHost() ? DIALER_CONSOLE_PATH : routes.dashboard.path);
  };

  // Check for logout notification from URL params
  useEffect(() => {
    const reason = searchParams.get('reason');
    const loggedOut = searchParams.get('logged_out');
    const message = searchParams.get('message');

    if (reason || loggedOut || message) {
      setShowLogoutNotification(true);
      // If there's a custom message, use it as the reason
      if (message) {
        setLogoutReason(message);
      } else {
        setLogoutReason(reason);
      }

      // Clear the URL params without refreshing the page
      const url = new URL(window.location.href);
      url.searchParams.delete('reason');
      url.searchParams.delete('logged_out');
      url.searchParams.delete('message');
      window.history.replaceState({}, '', url.toString());
    }
  }, [searchParams]);

  // Handle failed login attempt - hybrid tracking (backend + local)
  const handleFailedLogin = async (email: string) => {
    // Track failed attempt on backend for cross-device lockout
    const result = await supabaseAuthService.trackFailedLogin(email);

    // Always increment local attempts for immediate UI feedback
    // This handles both:
    // 1. Non-existent users (backend always returns 4 remaining)
    // 2. Cases where backend tracking might fail
    const newLocalAttempts = loginAttempts + 1;

    if (result.locked) {
      // Backend confirmed account is locked
      const lockoutUntil = new Date(Date.now() + (result.lockoutMinutes || 15) * 60 * 1000);
      setLockoutTime(lockoutUntil);
      setIsLocked(true);
      setLoginAttempts(5);
      setError(`Too many failed attempts. Account locked for ${result.lockoutMinutes || 15} minutes.`);
      return;
    }

    // Check if backend tracking is working (remainingAttempts decreasing)
    // Backend returns 4 for non-existent users to prevent enumeration
    const backendRemainingAttempts = result.remainingAttempts;
    const backendAttempts = backendRemainingAttempts !== undefined ? (5 - backendRemainingAttempts) : 0;

    // Use the higher of backend or local attempts
    // This ensures non-existent users still get locked out locally
    const effectiveAttempts = Math.max(backendAttempts, newLocalAttempts);
    const effectiveRemaining = 5 - effectiveAttempts;

    setLoginAttempts(effectiveAttempts);

    if (effectiveAttempts >= 5) {
      const lockoutUntil = new Date(Date.now() + 15 * 60 * 1000);
      setLockoutTime(lockoutUntil);
      setIsLocked(true);
      setError("Too many failed attempts. Account locked for 15 minutes.");
    } else {
      setError(`Invalid email or password. ${effectiveRemaining} attempt${effectiveRemaining !== 1 ? 's' : ''} remaining.`);
    }
  };

  // Reset security state on successful login
  const resetSecurityState = () => {
    setLoginAttempts(0);
    setIsLocked(false);
    setLockoutTime(null);
    setTurnstileToken("");
    setIsTurnstileValid(false);
  };

  /**
   * Everything after a sign-in succeeds, for the password form and passkeys alike: pending
   * approval, the 2-step code, agency selection or the dashboard, in that order.
   */
  const handleSignedIn = async (
    response: Awaited<ReturnType<typeof supabaseAuthService.signInWithPassword>>,
    turnstileToken: string,
    deviceId: string,
  ) => {
    // Follow nextStep field from backend response
    const nextStep = response.nextStep;
    const user = response.user;
    const agencies = response.agencies;

    // FIRST: Check for pending approval - redirect to pending-approval page
    // Backend now returns nextStep = 'pending_approval' for pending users
    const accountStatus = (user as any)?.accountStatus;
    if (nextStep === "pending_approval" || accountStatus === "pending" || accountStatus === "pending_approval") {
      resetSecurityState();
      navigate.push('/auth/pending-approval');
      return;
    }

    // Handle MFA requirement - ONLY check nextStep (backend already determined if device is trusted)
    if (nextStep === "mfa") {
      setUserId(user?.id ?? "");
      setUserMfaSettings(user?.mfaSettings || null);

      // CRITICAL: Store the Supabase session for 2FA verification
      // The verify2FACode function needs this to set the user status after 2FA
      try {
        const pending = {
          userId: user?.id,
          session: response.session, // The Supabase session with access_token
          turnstileToken,
          deviceId,
        };
        sessionStorage.setItem('insuracentral_pending_2fa', JSON.stringify(pending));
      } catch {
        // Silent fail - session storage may not be available
      }

      setShow2FA(true);
      resetSecurityState(); // Reset on successful 2FA trigger
      return;
    }

    // Reset security state on successful login
    resetSecurityState();
    
    // Store user profile for context
    if (user) {
      const userProfile = {
        user: user,
        trialExpiresAt: (user as any)?.trialExpiresAt || "",
        isTrialActive: (user as any)?.isTrialActive || false,
      };
      refetchUser(() => userProfile as unknown as UserProfile);
    }

    // Handle agency selection flow
    if (nextStep === "agency_select" || (agencies && agencies.length > 0)) {
      // Convert response to expected format
      const convertedLoginData: LoginResponse = {
        user: {
          id: user?.id || "",
          name: user?.name || "",
          email: user?.email || "",
          title: (user as any)?.title || "",
          userType: user?.userType || "",
          profileImageUrl: (user as any)?.profileImageUrl,
          profile_image_url: (user as any)?.profile_image_url,
        },
        agencies: agencies || [],
        isMultiAgencyUser: !!(agencies && agencies.length > 1),
        isIndependentAgent: !agencies || agencies.length === 0,
      };
      setLoginData(convertedLoginData);
      setShowAgencySelection(true);
      return;
    }

    // If nextStep is dashboard or no specific step, go to dashboard
    if (nextStep === "dashboard" || !nextStep) {
      // reset security state on success
      setLoginAttempts(0);
      setIsLocked(false);
      setLockoutTime(null);
      setError("");
      // Mark page load time for trial expiration checks
      if (typeof window !== 'undefined') {
        (window as any).__PAGE_LOAD_TIME = Date.now();
        // Clear session tracking flags so SessionValidator sees this as fresh login
        clearSessionStorage();
        sessionStorage.removeItem('insuracentral_active_tab_id');
        // Stamp the shared activity clock so a stale pre-logout value can't
        // trigger an instant auto-logout right after this fresh login
        try { localStorage.setItem('insuracentral_last_activity', Date.now().toString()); } catch {}
      }
      navigate.push(direct130Next.current || routes.dashboard.path);
      return;
    }

    // Default fallback
    setLoginAttempts(0);
    setIsLocked(false);
    setLockoutTime(null);
    setError("");
    // Clear session tracking flags so SessionValidator sees this as fresh login
    if (typeof window !== 'undefined') {
      clearSessionStorage();
      sessionStorage.removeItem('insuracentral_active_tab_id');
      // Stamp the shared activity clock so a stale pre-logout value can't
      // trigger an instant auto-logout right after this fresh login
      try { localStorage.setItem('insuracentral_last_activity', Date.now().toString()); } catch {}
    }
    navigate.push(direct130Next.current || routes.dashboard.path);
  };

  // Direct 130's CRM address: no password form, the agent signs in through Direct 130. Its "Open your CRM"
  // lands here with a one-time token in the hash (never sent to a server log), which signs them straight in.
  const onDirect130 = useDirect130Host();
  const [direct130Busy, setDirect130Busy] = useState(false);
  useEffect(() => {
    if (!onDirect130) return;
    const hash = new URLSearchParams(window.location.hash.slice(1));
    const tokenHash = hash.get("direct130");
    if (!tokenHash) return;
    // Only the pages Direct 130's dashboard sends (its lib/crm.js CRM_PAGES), matched exactly after the browser
    // normalizes the path: a same-origin check alone passes "/.//evil.com", which becomes the protocol-relative
    // "//evil.com". Anything else lands on the dashboard.
    let next = "";
    try {
      const url = new URL(hash.get("next") || "", window.location.origin);
      const target = url.pathname + url.search;
      if (url.origin === window.location.origin && ["/quote-enroll", "/leads", "/settings?tab=dial-credits"].includes(target)) next = target;
    } catch { /* not one of them */ }
    window.history.replaceState(null, "", window.location.pathname + window.location.search);
    setDirect130Busy(true);
    const deviceId = getOrCreateDeviceId();
    supabaseAuthService
      .signInWithLinkToken(tokenHash, deviceId)
      .then((response) => {
        if (response.error) return setError(response.error);
        direct130Next.current = next; // only once the one-time link itself signed them in
        return handleSignedIn(response, "", deviceId);
      })
      .catch(() => setError("You're signed in, but your account didn't open. Refresh the page to continue."))
      .finally(() => setDirect130Busy(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Passkeys, web only: the native app's WebView can't reach the device's passkeys.
  // passkeyAutofill = the browser offers saved passkeys in the email box's autofill. That request
  // waits in the background until someone picks one, so the button aborts it before its own prompt.
  const [passkeysSupported, setPasskeysSupported] = useState(false);
  const [passkeyAutofill, setPasskeyAutofill] = useState(false);
  const [passkeyBusy, setPasskeyBusy] = useState(false);
  // Shown when the browser's passkey window closes without signing in. Browsers don't tell a site
  // whether the person HAS a passkey (privacy), so "no passkey yet" and "closed it" look the same.
  const [passkeyHint, setPasskeyHint] = useState("");
  const passkeyRequest = useRef<AbortController | null>(null);

  useEffect(() => {
    if (isNativePlatform() || !window.PublicKeyCredential) return;
    setPasskeysSupported(true);
    PublicKeyCredential.isConditionalMediationAvailable?.()
      .then((ok) => setPasskeyAutofill(!!ok))
      .catch(() => undefined);
  }, []);

  const signInWithPasskey = async (conditional: boolean) => {
    passkeyRequest.current?.abort();
    const request = new AbortController();
    passkeyRequest.current = request;
    if (!conditional) {
      setError("");
      setPasskeyHint("");
      setPasskeyBusy(true);
    }
    const deviceId = getOrCreateDeviceId();
    const response = await supabaseAuthService.signInWithPasskey(deviceId, { conditional, signal: request.signal });
    if (!conditional) setPasskeyBusy(false);
    if (request.signal.aborted) return; // replaced by another request, or the form went away
    passkeyRequest.current = null;
    if (response.error || response.cancelled) {
      if (response.cancelled && !conditional) {
        setPasskeyHint("No passkey was used. To set one up, sign in with your password, then go to Settings → Security → Passkeys and select Add a passkey.");
      }
      // The autofill request only speaks up when a passkey was picked after its challenge expired;
      // anything else it hits before a pick (passkeys off, offline) stays quiet. The button reports all.
      if (response.error && (!conditional || response.expired) && !response.cancelled) {
        const locked = response.error.startsWith("ACCOUNT_LOCKED:");
        setError(
          locked
            ? `Account locked due to too many failed attempts. Try again in ${parseInt(response.error.split(":")[1], 10) || 15} minutes.`
            : response.error,
        );
      }
      if (passkeyAutofill && (!conditional || response.expired)) signInWithPasskey(true);
      return;
    }
    try {
      await handleSignedIn(response, "", deviceId);
    } catch {
      setError("You're signed in, but your account didn't open. Refresh the page to continue.");
    }
  };

  // Arm the autofill while the sign-in form is on screen (the email box then carries "webauthn").
  const passkeyAutofillOn = passkeyAutofill && !onDirect130 && !showForgotPassword && !isAgencySelectionMode && !show2FA && !showAgencySelection;
  useEffect(() => {
    if (!passkeyAutofillOn) return;
    signInWithPasskey(true);
    return () => passkeyRequest.current?.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [passkeyAutofillOn]);

  const onSubmit = async (values: TLoginSchema) => {
    try {
      setError("");
      setPasskeyHint("");

      // Honeypot: bots fill the hidden `website` field. Block client-side with a
      // generic error — sign-in goes through Supabase directly, so there's no
      // backend call to flag it (the /auth/login endpoint still traps direct-API
      // bots separately).
      if (honeypot.trim()) {
        setError("Invalid email or password.");
        return;
      }

      // CRITICAL: Check backend lockout status BEFORE attempting Supabase auth
      // This ensures lockout persists across page refresh/new tabs
      // Must use return value since React state updates are async
      const isLockedByBackend = await checkBackendLockoutStatus(values.username);
      if (isLockedByBackend) {
        return; // Backend confirmed account is locked - don't attempt login
      }

      // Also check local lockout state (for non-existent users tracked locally)
      if (checkLockout()) {
        return;
      }

      // Only require Turnstile validation for initial login, not for agency selection
      // Skip Turnstile in development (localhost)
      const isDevelopment = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
      if (!isAgencySelectionMode && !isTurnstileValid && !isDevelopment) {
        setError("Please complete the security verification");
        return;
      }

      // Add Turnstile token to login request
      // Generate a device ID based on browser/device characteristics
      // Use centralized deviceId generation to ensure consistency across login and 2FA flows
      const deviceId = getOrCreateDeviceId();

      const loginData = {
        ...values,
        turnstileToken,
        deviceId,
      };

      const response = await supabaseAuthService.signInWithPassword(
        values.username, // email
        values.password,
        deviceId,
        turnstileToken
      );

      // Check for errors first
      if (response.error) {
        throw new Error(response.error);
      }
      
      await handleSignedIn(response, turnstileToken, deviceId);

    } catch (err: unknown) {
      let errorMessage = "An error occurred during login";

      if (err instanceof Error) {
        errorMessage = err.message;
      } else if (typeof err === "string") {
        errorMessage = err;
      } else if (err && typeof err === "object") {
        const errorObj = err as { response?: { data?: { message?: string } }; message?: string; error?: string };
        if (errorObj.response?.data?.message) {
          errorMessage = errorObj.response.data.message;
        } else if (errorObj.message) {
          errorMessage = errorObj.message;
        } else if (errorObj.error) {
          errorMessage = errorObj.error;
        }
      }

      // A DEACTIVATED account is not a failed login attempt.
      //
      // Sign-in talks to Supabase directly, so a banned user gets GoTrue's raw
      // wording ("User is banned") passed straight through to the form — three
      // blunt words, no branding, and no idea who to contact. Worse, it used to
      // fall into handleFailedLogin() below and burn one of their five attempts,
      // eventually stacking a bogus "too many failed attempts" lockout on top of
      // a deactivation. The password was correct; there is nothing to retry.
      const isDeactivated =
        /user is banned|user_banned/i.test(errorMessage) ||
        /account (has been )?(deactivated|suspended)/i.test(errorMessage);

      // AN OUTAGE IS NOT A WRONG PASSWORD.
      //
      // Everything that is not ACCOUNT_LOCKED or deactivated fell into
      // handleFailedLogin() below, which tells the user "Invalid email or
      // password" AND burns one of their five attempts. So when the backend
      // cannot reach the database we accuse them of mistyping a password that
      // was never checked — and after five tries lock them out for 15 minutes
      // over an infrastructure fault.
      //
      // Exactly what happened on 2026-08-28: the Supabase pooler refused
      // connections for ~2 minutes (16:36-16:37 UTC, :econnrefused / SQLSTATE
      // 08006; Postgres itself never restarted — 35 days uptime). /auth/profile
      // failed, ~30 users were signed out, and this form reported a database
      // outage as bad credentials. It reached us as "it signed everyone out and
      // said wrong password".
      //
      // These are transport/server failures: the credentials were never
      // evaluated, so there is nothing to correct and no attempt to count.
      const errStatus =
        (err as { status?: number; response?: { status?: number } })?.status ??
        (err as { response?: { status?: number } })?.response?.status ??
        0;
      const isInfrastructure =
        !isDeactivated &&
        (/failed to fetch|networkerror|network request failed|load failed/i.test(errorMessage) ||
          /econnrefused|econnreset|etimedout|enotfound|socket hang up/i.test(errorMessage) ||
          /failed to connect to database|connection (refused|error|terminated)/i.test(errorMessage) ||
          /timed? ?out/i.test(errorMessage) ||
          /internal server error|bad gateway|service unavailable|gateway timeout/i.test(errorMessage) ||
          [500, 502, 503, 504].includes(errStatus));


      // Check if this is an ACCOUNT_LOCKED error from backend
      if (errorMessage.startsWith('ACCOUNT_LOCKED:')) {
        const lockoutMinutes = parseInt(errorMessage.split(':')[1], 10) || 15;
        const lockoutUntil = new Date(Date.now() + lockoutMinutes * 60 * 1000);
        setLockoutTime(lockoutUntil);
        setIsLocked(true);
        setLoginAttempts(5);
        setError(`Account locked due to too many failed attempts. Try again in ${lockoutMinutes} minutes.`);
      } else if (isDeactivated) {
        setError(
          "This account has been deactivated. Please contact your agency owner if you believe this is a mistake.",
        );
      } else if (errorMessage === NETWORK_BLOCKED_MESSAGE) {
        // Banned network (VPN/proxy), not a wrong password: no attempt is consumed.
        setError(errorMessage);
      } else if (isInfrastructure) {
        // Deliberately NOT handleFailedLogin() — no attempt is consumed.
        setError(
          "We couldn't reach the server, so your sign-in wasn't completed. This is on our side, not your password — please try again in a moment.",
        );
      } else {
        // Handle failed login attempt - pass email to track on backend
        await handleFailedLogin(values.username);
      }
      
      // Reset Turnstile on failed attempt to allow retry
      // This will trigger the widget to get a fresh token
      if (turnstileRef.current) {
        turnstileRef.current.reset();
      }
      setTurnstileToken("");
      setIsTurnstileValid(false);
      
      // Clear only password field for quick retry
      try {
        form.setValue('password', '', { shouldValidate: true });
        // Re-trigger form validation to ensure button becomes enabled
        setTimeout(() => {
          form.trigger();
        }, 100);
      } catch (formError) {
        if (process.env.NODE_ENV === 'production') {

        }
      }
      
      toast({
        title: isInfrastructure ? "Server Unavailable" : "Login Failed",
        description: isInfrastructure
          ? "We couldn't reach the server. Your password is fine — please try again shortly."
          : errorMessage,
        className: isInfrastructure
          ? "max-w-sm bg-amber-500 border-amber-500 text-white shadow-lg"
          : "max-w-sm bg-red-500 border-red-500 text-white shadow-lg",
      });
    } finally {
      // Ensure form is not stuck in submitting state
      // This is a safety net in case the form doesn't reset properly
    }
  };

  const handleSocialLogin = async (provider: "google" | "apple" | "microsoft") => {
    try {
      setError("");
      
      let response;
      switch (provider) {
        case "google":
          response = await oauthService.signInWithGoogle();
          break;
        case "apple":
          response = await oauthService.signInWithApple();
          break;
        case "microsoft":
          response = await oauthService.signInWithMicrosoft();
          break;
        default:
          throw new Error("Unsupported provider");
      }
      
      if (response.error) {
        throw response.error;
      }
      
      // The OAuth service will redirect to the callback page
      // No need to handle the response here as the callback page will handle authentication
    } catch (err: unknown) {
      let errorMessage = "Social login failed";

      if (err instanceof Error) {
        errorMessage = err.message;
      }

      // Same deactivation wording as the password flow — a banned account is
      // banned whichever button they press, and "Social Login Failed: User is
      // banned" would send them retrying Google forever.
      const isDeactivated =
        /user is banned|user_banned/i.test(errorMessage) ||
        /account (has been )?(deactivated|suspended)/i.test(errorMessage);
      if (isDeactivated) {
        errorMessage =
          "This account has been deactivated. Please contact your agency owner if you believe this is a mistake.";
      }

      setError(errorMessage);
      toast({
        title: isDeactivated ? "Account Deactivated" : "Social Login Failed",
        description: errorMessage,
        className: "max-w-sm bg-red-500 border-red-500 text-white shadow-lg",
      });
    }
  };

  const handle2FABack = () => {
    setShow2FA(false);
    setUserId("");
    form.reset();
  };

  const handle2FASuccess = async (fallbackToken?: string) => {
    // 2FA success - check for agency selection
    try {
      // NEVER hide 2FA screen - let hard navigation handle the transition
      // Hiding it would briefly show the login form, causing a flash

      // Get deviceId in this function's scope for device trust check
      const deviceId = getOrCreateDeviceId();

      // Get session token - try multiple sources:
      // 1. Supabase client session (ideal - verify2FACode set it successfully)
      // 2. fallbackToken passed from TwoFactorAuth (saved BEFORE verify2FACode ran)
      // 3. refreshSession (last resort)
      let sessionToken = (await supabaseAuthService.getCurrentSession())?.access_token;

      if (!sessionToken && fallbackToken) {
        sessionToken = fallbackToken;
      }

      if (!sessionToken) {
        const refreshed = await supabaseAuthService.refreshSession();
        sessionToken = refreshed?.access_token || "";
      }

      // Note: If fallback token was used, the axios interceptor will also pick it up
      // from the pending 2FA storage via supabaseAuthService.getAccessToken() fallback.
      // No need to call setSession() here - it already failed in verify2FACode.

      const profileResponse = await supabaseAuthService.getUserProfile(
        sessionToken || "",
        deviceId // Pass deviceId for device trust check
      );

      // DON'T hide 2FA screen here - only hide it when showing agency selection
      // For dashboard navigation, the window.location.href will handle the transition

      // Update user context with fresh profile data
      // IMPORTANT: Force userStatus to 'available' - we just logged in successfully
      // Don't trust the profile response for this field due to timing/race condition
      // (the status update API call may not have propagated to the profile fetch yet)
      if (profileResponse.user) {
        const userProfile = {
          user: {
            ...profileResponse.user,
            // Force 'available' status after successful login - database was already updated
            userStatus: 'available',
          },
          trialExpiresAt: (profileResponse.user as any)?.trialExpiresAt || "",
          isTrialActive: (profileResponse.user as any)?.isTrialActive || false,
        };
        refetchUser(() => userProfile as unknown as UserProfile);
      }

      // Check if user has agencies - if so, show agency selection
      if (profileResponse.agencies && Array.isArray(profileResponse.agencies) && profileResponse.agencies.length > 0) {
        // Convert response to expected format
        const convertedLoginData: LoginResponse = {
          user: {
            id: profileResponse.user?.id || "",
            name: profileResponse.user?.name || "",
            email: profileResponse.user?.email || "",
            title: (profileResponse.user as any)?.title || "",
            userType: profileResponse.user?.userType || "",
            profileImageUrl: (profileResponse.user as any)?.profileImageUrl,
            profile_image_url: (profileResponse.user as any)?.profile_image_url,
          },
          agencies: profileResponse.agencies,
          isMultiAgencyUser: profileResponse.agencies.length > 1,
          isIndependentAgent: false,
        };
        setLoginData(convertedLoginData);
        // DO NOT clean up pending 2FA storage here!
        // AgencySelection needs the fallback token via getAccessToken() since
        // the Supabase client may have no session (multi-device login scenario).
        // Cleanup happens in handleAgencySelectionSuccess after agency is selected.
        // Only hide 2FA when transitioning to agency selection (not for dashboard navigation)
        setShow2FA(false);
        setShowAgencySelection(true);
        return;
      }

      // If no agencies, go to dashboard
      // Force-write session to localStorage before navigating (bypasses Supabase server validation)
      try {
        const pendingRaw = sessionStorage.getItem('insuracentral_pending_2fa');
        if (pendingRaw) {
          const pending = JSON.parse(pendingRaw);
          const at = pending?.session?.access_token;
          const rt = pending?.session?.refresh_token;
          if (at && rt) {
            supabaseAuthService.forceWriteSessionToStorage(at, rt);
          }
        }
      } catch {}
      try { sessionStorage.removeItem('insuracentral_pending_2fa'); } catch {}
      // Clear session tracking flags so SessionValidator sees this as fresh login
      if (typeof window !== 'undefined') {
        clearSessionStorage();
        sessionStorage.removeItem('insuracentral_active_tab_id');
        // Stamp the shared activity clock so a stale pre-logout value can't
        // trigger an instant auto-logout right after this fresh login
        try { localStorage.setItem('insuracentral_last_activity', Date.now().toString()); } catch {}
      }
      // Use window.location.href for smooth transition (same as agency selection)
      finishLogin();
    } catch (error) {
      // Force-write session to localStorage before navigating
      try {
        const pendingRaw = sessionStorage.getItem('insuracentral_pending_2fa');
        if (pendingRaw) {
          const pending = JSON.parse(pendingRaw);
          const at = pending?.session?.access_token;
          const rt = pending?.session?.refresh_token;
          if (at && rt) {
            supabaseAuthService.forceWriteSessionToStorage(at, rt);
          }
        }
      } catch {}
      try { sessionStorage.removeItem('insuracentral_pending_2fa'); } catch {}
      // Clear session tracking flags so SessionValidator sees this as fresh login
      if (typeof window !== 'undefined') {
        clearSessionStorage();
        sessionStorage.removeItem('insuracentral_active_tab_id');
        // Stamp the shared activity clock so a stale pre-logout value can't
        // trigger an instant auto-logout right after this fresh login
        try { localStorage.setItem('insuracentral_last_activity', Date.now().toString()); } catch {}
      }
      // Use window.location.href for smooth transition (same as agency selection)
      finishLogin();
    }
  };

  const handleAgencySelectionBack = () => {
    setShowAgencySelection(false);
    setIsAgencySelectionMode(false);
    setLoginData(null);
    form.reset();
  };

  const handleAgencySelectionStart = () => {
    setIsAgencySelectionMode(true);
  };

  const handleAgencySelectionSuccess = async () => {
    // Reset agency selection mode and clear Turnstile state
    setIsAgencySelectionMode(false);
    setTurnstileToken("");
    setIsTurnstileValid(false);

    // CRITICAL: Restore Supabase session in localStorage before navigating to dashboard.
    // In multi-device login scenarios, setSession() fails because Supabase's auth server
    // rejects the invalidated session (it calls _getUser() which returns an error).
    // However, our backend still accepts the JWT (proven by all the 200 responses above).
    // Write the session directly to localStorage, bypassing Supabase's network validation.
    try {
      const pendingRaw = sessionStorage.getItem('insuracentral_pending_2fa');
      if (pendingRaw) {
        const pending = JSON.parse(pendingRaw);
        const accessToken = pending?.session?.access_token;
        const refreshToken = pending?.session?.refresh_token;
        if (accessToken && refreshToken) {
          supabaseAuthService.forceWriteSessionToStorage(accessToken, refreshToken);
        }
      }
    } catch {
      // Silent fail
    }

    // Clean up pending 2FA storage now that agency selection is complete
    try { sessionStorage.removeItem('insuracentral_pending_2fa'); } catch {}

    // Clear session tracking flags so SessionValidator sees this as fresh login
    if (typeof window !== 'undefined') {
      clearSessionStorage();
      sessionStorage.removeItem('insuracentral_active_tab_id');
      // Stamp the shared activity clock so a stale pre-logout value can't
      // trigger an instant auto-logout right after this fresh login
      try { localStorage.setItem('insuracentral_last_activity', Date.now().toString()); } catch {}
    }

    // Use only window.location.href for reliable navigation on Render
    finishLogin();
  };

  const handleForgotPassword = async () => {
    if (!forgotPasswordEmail) {
      setForgotPasswordMessage("Please enter your email address");
      return;
    }

    try {
      setForgotPasswordLoading(true);
      setForgotPasswordMessage("");
      
      await requestPasswordReset(forgotPasswordEmail);
      setForgotPasswordMessage("Password reset instructions have been sent to your email");
      
      // Reset form after successful submission
      setTimeout(() => {
        setShowForgotPassword(false);
        setForgotPasswordEmail("");
        setForgotPasswordMessage("");
      }, 3000);
      
    } catch (err: unknown) {
      let errorMessage = "Failed to send password reset email";
      
      if (err instanceof Error) {
        errorMessage = err.message;
      } else if (err && typeof err === "object") {
        const errorObj = err as { response?: { data?: { message?: string } }; message?: string };
        if (errorObj.response?.data?.message) {
          errorMessage = errorObj.response.data.message;
        } else if (errorObj.message) {
          errorMessage = errorObj.message;
        }
      }
      
      setForgotPasswordMessage(errorMessage);
    } finally {
      setForgotPasswordLoading(false);
    }
  };

  // Direct 130's address: its own page in its site's style (www.direct130.com/login) around each step.
  // globals.css forces every <main> to min-height auto and max-width 100vw (!important): the page color sits on
  // the div, and the column width needs the ! to win.
  const direct130Page = (content: React.ReactNode) => (
    <div className="min-h-[100dvh] bg-[#f2f6f4] px-4 py-12 text-[#202c2d] flex items-center justify-center">
      <main className="w-full !max-w-[420px]">
        {/* eslint-disable-next-line @next/next/no-img-element -- a fixed local wordmark, sized by height */}
        <img src="/direct130/logo.png" alt="Direct130" width={431} height={132} className="mx-auto mb-8 h-11 w-auto" />
        {content}
      </main>
    </div>
  );

  if (show2FA && onDirect130) {
    return direct130Page(
      <section className={DIRECT130_CARD}>
        <TwoFactorAuth direct130 onBack={handle2FABack} onSuccess={handle2FASuccess} userId={userId} userMfaSettings={userMfaSettings} />
      </section>,
    );
  }

  if (show2FA) {
    return (
      <div className={`native-auth-scope min-h-screen flex items-start sm:items-center justify-center bg-gradient-to-br from-primary/5 via-white to-primary/10 px-3 sm:px-4 py-4 sm:py-8 md:py-12 overflow-y-auto`}>
        <div className="w-full max-w-xl bg-white rounded-xl sm:rounded-2xl shadow-xl border border-primary/10 p-4 sm:p-6 md:p-8 my-auto">
          <TwoFactorAuth
            onBack={handle2FABack}
            onSuccess={handle2FASuccess}
            userId={userId}
            userMfaSettings={userMfaSettings}
          />
        </div>
      </div>
    );
  }

  if (showAgencySelection && loginData && !onDirect130) {
    return (
      <div className={`native-auth-scope min-h-screen flex items-start sm:items-center justify-center bg-gradient-to-br from-primary/5 via-white to-primary/10 px-3 sm:px-4 py-4 sm:py-8 md:py-12 overflow-y-auto`}>
        <div className="w-full max-w-xl bg-white rounded-xl sm:rounded-2xl shadow-xl border border-primary/10 p-4 sm:p-6 md:p-8 my-auto">
          <AgencySelection
            loginData={loginData}
            onBack={handleAgencySelectionBack}
            onSuccess={handleAgencySelectionSuccess}
            onStart={handleAgencySelectionStart}
          />
        </div>
      </div>
    );
  }

  // Direct 130's sign-in: the one way in.
  if (onDirect130) {
    return direct130Page(
      <>
        <section aria-labelledby="d130-signin-title" className={DIRECT130_CARD}>
          <h1 id="d130-signin-title" className="!mb-6 !text-[26px] !font-normal !leading-tight">
            Built for producers.
          </h1>
          {showLogoutNotification && !error && DIRECT130_SIGNED_OUT[logoutReason ?? ""] && (
            <p className="mb-4 rounded-lg bg-[#f6e8d4] px-3 py-2.5 text-[14px] text-[#5c4318]" role="status">
              {DIRECT130_SIGNED_OUT[logoutReason ?? ""]}
            </p>
          )}
          {!!error && (
            <p className="mb-4 rounded-lg bg-[#fbeaea] px-3 py-2.5 text-[14px] text-[#8a1f1f]" role="alert">
              {error}
            </p>
          )}
          {showAgencySelection && loginData ? (
            <AgencySelection
              loginData={loginData}
              autoSelect
              onBack={handleAgencySelectionBack}
              onSuccess={handleAgencySelectionSuccess}
              onStart={handleAgencySelectionStart}
            />
          ) : direct130Busy ? (
            <div className="flex min-h-[46px] items-center justify-center gap-2 text-[15px] text-[#566465]" role="status">
              <LoadingSpinner size="md" /> Signing you in…
            </div>
          ) : (
            <a
              href={DIRECT130_SIGN_IN_URL}
              className={DIRECT130_BUTTON}
            >
              Sign in with Direct130
            </a>
          )}
        </section>
        <p className="mt-6 flex flex-wrap items-center justify-center gap-3 text-[15px] text-[#566465]">
          New to Direct130?
          <a href={DIRECT130_APPLY_URL} className={DIRECT130_BOX}>
            Check eligibility
          </a>
        </p>
      </>,
    );
  }

  return (
    <>
      <style jsx global>{`
        /* Aggressive scaling for signin card to fit viewport height without scrolling */
        @media (max-height: 900px) {
          .signin-card { zoom: 0.92; }
        }
        @media (max-height: 800px) {
          .signin-card { zoom: 0.85; }
        }
        @media (max-height: 700px) {
          .signin-card { zoom: 0.75; }
        }
        @media (max-height: 600px) {
          .signin-card { zoom: 0.65; }
        }
        @media (max-height: 500px) {
          .signin-card { zoom: 0.55; }
        }
        @media (max-height: 400px) {
          .signin-card { zoom: 0.45; }
        }
        /* Firefox fallback (doesn't support zoom) */
        @supports not (zoom: 1) {
          @media (max-height: 800px) {
            .signin-card {
              transform: scale(0.8);
              transform-origin: top center;
            }
          }
          @media (max-height: 600px) {
            .signin-card {
              transform: scale(0.6);
              transform-origin: top center;
            }
          }
        }
      `}</style>
      <div className={`native-auth-scope min-h-[100dvh] flex items-center justify-center bg-gradient-to-br from-primary/5 via-white to-primary/10 px-1 py-1 overflow-y-auto`}>
        <div className="signin-card w-full max-w-[99vw] sm:max-w-md md:max-w-lg lg:max-w-xl bg-white rounded-lg sm:rounded-xl md:rounded-2xl shadow-xl border border-primary/10 p-2 sm:p-3 md:p-5 space-y-1.5 sm:space-y-3 md:space-y-5">
          {/* One block so short-landscape native can sit it beside the form (globals.css). */}
          <div className="signin-brand space-y-1.5 sm:space-y-3 md:space-y-5">
            {/* Native hero — the phone screen had a dead top half and a
                postage-stamp logo. Big centered mark + brand name fills it the
                way a native login expects. Same branding sources as the inline
                row below, so white-label (MRFG etc.) shows THEIR logo, never
                ours. */}
            <div className="native-only flex-col items-center gap-4 pb-2 pt-1">
                <Image
                  alt={branding.brandName}
                  className="h-24 w-24 rounded-2xl object-contain"
                  height={192}
                  width={192}
                  src={branding.logoUrl || branding.faviconUrl || "/favicon.png"}
                  unoptimized
                  priority
                />
                {isWhiteLabeled ? (
                  <span className="text-3xl font-bold tracking-tight text-primary">{branding.brandName}</span>
                ) : (
                  <span className="text-3xl font-bold tracking-tight">
                    <span className="text-[#0a2540]">Insura</span>
                    <span className="text-[#0096c7]">Central</span>
                  </span>
                )}
            </div>
            <div className="web-only flex items-center gap-1">
              <Image
                alt={branding.brandName}
                className="rounded h-4 w-4 sm:h-5 sm:w-5 md:h-7 md:w-7"
                height={32}
                width={32}
                src={branding.logoUrl || branding.faviconUrl || "/favicon.png"}
                unoptimized
                priority
              />
              {isWhiteLabeled ? (
                <span className="text-base sm:text-lg md:text-xl lg:text-2xl font-bold text-primary">{branding.brandName}</span>
              ) : (
                <span className="text-base sm:text-lg md:text-xl lg:text-2xl font-bold">
                  <span className="text-[#0a2540]">Insura</span>
                  <span className="text-[#0096c7]">Central</span>
                </span>
              )}
            </div>

            {/* Hidden in reset mode — "Let's Get Started!" stacked above
                "Reset your password" was two competing headings. */}
            {!showForgotPassword && (
              <div className="native-center space-y-0.5">
                <h2 className="text-base sm:text-lg md:text-xl lg:text-2xl font-bold text-gray-900">
                  Let&apos;s Get Started!
                </h2>
                <p className="text-xs sm:text-sm text-gray-600">Sign into your account here.</p>
              </div>
            )}
          </div>

          {/* Session Logout Notification */}
          {showLogoutNotification && (
            <div className="p-3 rounded-lg bg-amber-50 border border-amber-200 text-amber-800">
              <div className="flex items-start gap-2">
                <svg className="h-5 w-5 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z" />
                </svg>
                <div className="flex-1">
                  <p className="font-medium text-sm">
                    {logoutReason === 'mfa_required'
                      ? 'Verify it\u2019s you'
                      : 'You have been logged out'}
                  </p>
                  <p className="text-xs mt-0.5 text-amber-700">
                    {logoutReason === 'session_expired'
                      ? 'Your session has expired. Please sign in again.'
                      : logoutReason === 'idle_timeout'
                      ? 'You were logged out due to inactivity. Please sign in again.'
                      : logoutReason === 'account_deactivated'
                      ? 'Your account has been deactivated. Please contact support.'
                      : logoutReason === 'logged_in_elsewhere'
                      ? 'You have been signed out because your account was logged in from another device.'
                      : logoutReason === 'network_blocked'
                      ? NETWORK_BLOCKED_MESSAGE
                      : logoutReason === 'mfa_required'
                      ? 'Sign in and enter your authenticator code to continue.'
                      /* Never render a raw reason code — an unknown value used to
                         print verbatim, so a redirect carrying `mfa_required`
                         showed the user the literal string with the underscore. */
                      : 'Please sign in to continue.'}
                  </p>
                </div>
                <button
                  onClick={() => setShowLogoutNotification(false)}
                  className="text-amber-600 hover:text-amber-800"
                >
                  <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>
          )}

          {!showForgotPassword ? (
            <Form {...form}>
              <form className="space-y-6" onSubmit={handleSubmit(onSubmit)}>
                <HoneypotField value={honeypot} onChange={setHoneypot} />
                <FormField
                  control={control}
                  name="username"
                  render={({ field }) => (
                    <FormInput
                      required
                      containerClass="space-y-2"
                      error={errors.username}
                      id="username"
                      // ⚠️ These two attributes ARE the Face ID sign-in
                      // experience. autoComplete="username" +
                      // "current-password" is what makes iOS offer the
                      // saved credential above the keyboard (Face ID
                      // confirms, both fields fill). type="username" was
                      // also invalid HTML — browsers fell back to text.
                      // "webauthn" adds saved passkeys to the same list, and
                      // only where the browser says it supports that.
                      autoComplete={passkeyAutofill ? "username webauthn" : "username"}
                      inputMode="email"
                      label="Email"
                      placeholder="john.doe@example.com"
                      type="email"
                      disabled={isLocked}
                      {...field}
                      onBlur={(e) => {
                        field.onBlur();
                        // Check backend lockout status when email field loses focus
                        checkBackendLockoutStatus(e.target.value);
                      }}
                    />
                  )}
                />

                <FormField
                  control={control}
                  name="password"
                  render={({ field }) => (
                    <FormInput
                      required
                      containerClass="space-y-2"
                      error={errors.password}
                      id="password"
                      label="Password"
                      placeholder="Enter your password"
                      type="password"
                      autoComplete="current-password"
                      disabled={isLocked}
                      {...field}
                    />
                  )}
                />

                {!!error && (
                  <div className="text-red-600 !mt-4 !p-2.5 bg-[#ffebee] !rounded">
                    {error}
                  </div>
                )}

                {/*
                  Lockout status. Deliberately rendered AFTER the error: the old
                  version sat above it, so a failed sign-in led with
                  "Security Notice: 4 attempts remaining" and buried the reason
                  the attempt failed. Order is now cause, then consequence.
                  Tone escalates with risk and every state offers a way out
                  (password reset) rather than just counting down at the user.
                */}
                {loginAttempts > 0 && (() => {
                  const left = Math.max(0, 5 - loginAttempts);
                  const mins = Math.floor(lockoutRemainingSeconds / 60);
                  const secs = lockoutRemainingSeconds % 60;
                  const tone = isLocked
                    ? { bar: "bg-red-500", box: "border-red-200 bg-red-50", head: "text-red-800", body: "text-red-700" }
                    : left <= 1
                      ? { bar: "bg-red-500", box: "border-red-200 bg-red-50", head: "text-red-800", body: "text-red-700" }
                      : { bar: "bg-amber-500", box: "border-amber-200 bg-amber-50", head: "text-amber-900", body: "text-amber-800" };
                  return (
                    <div className={`relative mt-3 overflow-hidden rounded-lg border ${tone.box}`}>
                      <div className={`absolute inset-y-0 left-0 w-1 ${tone.bar}`} />
                      <div className="py-3 pl-4 pr-3">
                        {isLocked ? (
                          <>
                            <p className={`text-sm font-semibold ${tone.head}`}>
                              Account locked for {mins > 0 ? `${mins}:${String(secs).padStart(2, "0")}` : `${secs}s`}
                            </p>
                            <p className={`mt-1 text-[13px] leading-relaxed ${tone.body}`}>
                              Too many failed sign-in attempts. You can wait it out, or reset your
                              password now to get straight back in.
                            </p>
                          </>
                        ) : (
                          <>
                            <p className={`text-sm font-semibold ${tone.head}`}>
                              {left === 1
                                ? "Last attempt before this account locks"
                                : `${left} attempts left`}
                            </p>
                            <p className={`mt-1 text-[13px] leading-relaxed ${tone.body}`}>
                              {left === 1
                                ? "One more wrong password locks this account for 15 minutes."
                                : "After 5 failed attempts this account locks for 15 minutes."}
                            </p>
                          </>
                        )}
                        {(isLocked || left <= 2) && (
                          <button
                            type="button"
                            onClick={() => setShowForgotPassword(true)}
                            className={`mt-2 text-[13px] font-semibold underline underline-offset-2 ${tone.head}`}
                          >
                            Reset your password
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })()}

                {/* Turnstile CAPTCHA - only show during initial login, never during agency selection */}
                {/* Uses ref to reset widget after failed login (tokens are single-use) */}
                {!isAgencySelectionMode && (
                  <div className="space-y-2">
                    <TurnstileWidget
                      ref={turnstileRef}
                      onToken={handleTurnstileToken}
                      setError={handleTurnstileError}
                    />
                  </div>
                )}

                <Button
                  className="w-full bg-slate-800 hover:bg-slate-900 disabled:opacity-50 disabled:cursor-not-allowed text-white font-medium py-3 px-4 rounded-lg transition-all duration-200 flex items-center justify-center space-x-2 group"
                  disabled={isLocked || !isValid || isSubmitting || (!isAgencySelectionMode && !isTurnstileValid)}
                  type="submit"
                >
                  {isLocked ? 'Account Locked' : 'Sign in'}
                  <span>{!!isSubmitting && <LoadingSpinner size="md" />}</span>
                </Button>

                {passkeysSupported && !isAgencySelectionMode && (
                  <Button
                    type="button"
                    variant="outline"
                    className="w-full !mt-3 rounded-lg py-3"
                    onClick={() => signInWithPasskey(false)}
                    disabled={passkeyBusy}
                  >
                    <KeyRound className="h-4 w-4" />
                    {passkeyBusy ? "Waiting for your passkey…" : "Sign in with a passkey"}
                  </Button>
                )}

                {!!passkeyHint && (
                  <p className="!mt-2 text-center text-[13px] leading-relaxed text-slate-600" role="status">
                    {passkeyHint}
                  </p>
                )}
              </form>
            </Form>
          ) : (
            <div className="space-y-6">
              <div className="text-center">
                <h3 className="text-lg font-semibold text-gray-900">Reset your password</h3>
                <p className="text-sm text-gray-600 mt-1">
                  Enter your email address and we'll send you instructions to reset your password.
                </p>
              </div>

              <div className="space-y-4">
                <div>
                  <label htmlFor="forgot-email" className="block text-sm font-medium text-gray-700 mb-1">
                    Email address
                  </label>
                  <input
                    id="forgot-email"
                    type="email"
                    value={forgotPasswordEmail}
                    onChange={(e) => setForgotPasswordEmail(e.target.value)}
                    placeholder="john.doe@example.com"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-transparent"
                    required
                  />
                </div>

                {forgotPasswordMessage && (
                  <div className={`p-3 rounded-lg text-sm ${
                    forgotPasswordMessage.includes("sent") 
                      ? 'bg-green-50 border border-green-200 text-green-700'
                      : 'bg-red-50 border border-red-200 text-red-700'
                  }`}>
                    {forgotPasswordMessage}
                  </div>
                )}

                <div className="flex space-x-3">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => {
                      setShowForgotPassword(false);
                      setForgotPasswordEmail("");
                      setForgotPasswordMessage("");
                    }}
                    className="flex-1"
                  >
                    Back to Sign In
                  </Button>
                  <Button
                    type="button"
                    onClick={handleForgotPassword}
                    disabled={forgotPasswordLoading || !forgotPasswordEmail}
                    className="flex-1 bg-slate-800 hover:bg-slate-900"
                  >
                    {forgotPasswordLoading ? (
                      <>
                        <LoadingSpinner size="sm" />
                        Sending...
                      </>
                    ) : (
                      "Send Reset Instructions"
                    )}
                  </Button>
                </div>
              </div>
            </div>
          )}

          {/* OAuth buttons hidden - temporarily disabled */}

          <div className="text-center space-y-2">
            {/* Signup is allowed in-app: it's genuinely free — no card is
                collected anywhere in the flow, and on native the plan step
                renders WITHOUT prices (Apple 3.1.1). "Free trial" wording is
                fine; naming a price would not be. */}
            {(
              <p className="text-sm text-gray-600">
                Don&apos;t have an account?{" "}
                <button
                  className="text-primary hover:text-primary/80 font-medium"
                  onClick={() => {
                    // Native app: no in-app signup — pop the "available on the
                    // web version" sheet whose Continue opens the system
                    // browser at the signup page. Web keeps the in-app flow.
                    if (!allowCheckout("signup")) return;
                    navigate.push(routes.auth.signUp.path);
                  }}
                >
                  Start your 14-day free trial
                </button>
              </p>
            )}
            <button
              type="button"
              className="text-sm text-gray-500 hover:text-gray-700"
              onClick={() => setShowForgotPassword(true)}
            >
              Forgot your password?
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default LoginScreen;